<template>
  <v-modal
    handle-click-bg
    full
    position="bottom"
    block-scroll
    animation="slide-up"
    min-width="50rem"
    v-model="show"
  >
    <template #header>
      {{ $t('components.Menu.manageTerminals') }}
    </template>

    <div class="terminals-list mx-auto my-1" style="max-width: 78rem" v-if="loading">
      <div class="reset-indent">
        <list-item loading v-for="(item, index) in 10" :key="index.toString() + '-' + item.toString() "></list-item>
      </div>
    </div>

    <div class="terminals-list mx-auto my-1" style="max-width: 78rem" v-else>
      <div class="reset-indent">
        <RecycleScroller
          :item-size="screen.breakPoint.hugeDesktop ? 72 : 65"
          :items="terminalList"
          :buffer="1000"
          class="scrollerarea"
          key-field="id"
          page-mode
          v-slot="{ item }"
        >
          <list-item one-columns>
            <template v-slot:firstColumn>
              <list-item-group>
                <list-item-content>
                  <list-item-title>
                    <span class="terminals-list__circle" :class="item.active ? 'terminals-list__circle--active' : 'terminals-list__circle--inactive' ">
                    </span> <span class="text--regular">№ </span>{{ item.computedNumber }}<span class="ml-1 icon-pin text--small"></span> <span class="text--regular text--small">{{ item.computedAddress }}</span>
                  </list-item-title>

                  <list-item-subtitle>
                    <span>{{ item.computedShopName }}</span>
                  </list-item-subtitle>
                </list-item-content>
              </list-item-group>
            </template>

            <template #controls v-if="ShowAddTerminal">
              <kebab-menu>
                <action-link color="success" v-if="ShowEditTerminalControl" icon-left="icon-edit" size="small" class="mb-1 pb-05" @click="handleUpdateTerminal(item)">{{ $t('components.TerminalList.edit') }}</action-link>
                <div class="divider reset-indent my-1" v-if="ShowEditTerminalControl"></div>
                <div class="d-flex justify-center">
                  <v-toggle :id="item.id.toString()" :value="item.active" @input="(val) => toggleActivity(val, item)">
                    <template #before>
                      {{item.active ? $t('components.TerminalList.active') : $t('components.TerminalList.inactive') }}
                    </template>
                  </v-toggle>
                </div>
              </kebab-menu>
            </template>
          </list-item>
        </RecycleScroller>

        <div class="mt-1"></div>

        <list-footer
          :loading="fetchingMore || $apollo.queries.terminals.loading"
          :total="!terminals ? 0 : terminals.totalElements"
          short
        ></list-footer>

        <div class="my-2"></div>
      </div>
    </div>

    <template #footer v-if="ShowAddTerminal">
      <div class="d-flex justify-end mx-auto" style="max-width: 78rem">
        <v-modal
          :animation="!screen.breakPoint.tabPort ? 'slide-up' : 'slide-down'"
          :width="!screen.breakPoint.tabPort ? '': 86"
          :full="!screen.breakPoint.tabPort"
          :noBackground="!screen.breakPoint.tabPort"
          :max-body-height="screen.breakPoint.tabPort ? '70vh' : null"
          v-model="showAddingTerminal"
          block-scroll
          theme="light-grey"
          handle-click-bg
        >
          <template #activator="{ on }">
            <v-button color="success" @click="on">
              <span class="d-flex align-center">
                <span class="mr-1 text--bold icon-plus d-block"></span>
                <span class="d-block">{{ $t('components.TerminalsList.addTerminal') }}</span>
              </span>
            </v-button>
          </template>

          <template #header>
            {{ $t('components.TerminalsList.addTerminal') }}
          </template>

          <template #footer>
            <div class="d-flex justify-end">
              <v-button @click="addTerminal()" :loading="addingTerminal">{{ $t('widgets.AddWidget.add') }}</v-button>
            </div>
          </template>

          <v-input
            :placeholder="$t('components.TerminalsList.terminalNumber')"
            :disabled="addingTerminal"
            :error="newTerminalError.number"
            class="mb-2"
            v-model="newTerminal.number"
          ></v-input>
          <autocomplete
            :placeholder="$t('components.TerminalsList.store')"
            :items="profile.shopList"
            :disabled="addingTerminal"
            :error="newTerminalError.shopId"
            v-model="newTerminal.shopId"
            value-field="id"
            item-text="name"
            position="fixed"
            custom-slot
          >
            <template #item="{ item }">
              <list-item one-column>
                <template #firstColumn>
                  <list-item-title>
                    <span>{{ item.name }}</span>
                  </list-item-title>
                  <list-item-subtitle>
                    <span class="icon-pin text--small"></span> <span class="text--regular text--small">{{ item.address }}</span>
                  </list-item-subtitle>
                </template>
              </list-item>
            </template>
          </autocomplete>
        </v-modal>
      </div>
    </template>

    <v-modal
      :animation="!screen.breakPoint.tabPort ? 'slide-up' : 'slide-down'"
      :width="!screen.breakPoint.tabPort ? '': 86"
      :full="!screen.breakPoint.tabPort"
      :noBackground="!screen.breakPoint.tabPort"
      :max-body-height="screen.breakPoint.tabPort ? '70vh' : null"
      v-model="showEditingTerminal"
      block-scroll
      theme="light-grey"
      handle-click-bg
    >
      <template #header>
        {{ $t('components.TerminalList.edit') }}
      </template>

      <template #footer>
        <div class="d-flex justify-end">
          <v-button @click="updateTerminal()" :loading="updatingTerminal">{{ $t('widgets.Dashboard.EditWidget.save') }}</v-button>
        </div>
      </template>

      <v-input
        :placeholder="$t('components.TerminalsList.terminalNumber')"
        :disabled="updatingTerminal"
        :error="editTerminalError.number"
        class="mb-2"
        v-model="editTerminal.number"
      ></v-input>
      <autocomplete
        :placeholder="$t('components.TerminalsList.store')"
        :items="profile.shopList"
        :disabled="updatingTerminal"
        :error="editTerminalError.shopId"
        v-model="editTerminal.shopId"
        value-field="id"
        item-text="name"
        position="fixed"
        not-empty
        custom-slot
      >
        <template #item="{ item }">
          <list-item one-column>
            <template #firstColumn>
              <list-item-title>
                <span>{{ item.name }}</span>
              </list-item-title>
              <list-item-subtitle>
                <span class="icon-pin text--small"></span> <span class="text--regular text--small">{{ item.address }}</span>
              </list-item-subtitle>
            </template>
          </list-item>
        </template>
      </autocomplete>
    </v-modal>
  </v-modal>
</template>

<script>
import vModal from '@/ui/modals/vModal'

import ListItem from '@/ui/slots/List/ListItem'
import ListItemGroup from '@/ui/slots/List/ListItemGroup'
import ListItemContent from '@/ui/slots/List/ListItemContent'
import ListItemTitle from '@/ui/slots/List/ListItemTitle'
import ListItemSubtitle from '@/ui/slots/List/ListItemSubtitle'
import ListFooter from '@/ui/slots/List/ListFooter'

import vToggle from '@/ui/controls/vToggle'
import Autocomplete from '@/ui/controls/Autocomplete'
import vInput from '@/ui/controls/vInput'
import KebabMenu from '@/ui/controls/KebabMenu'
import vButton from '@/ui/controls/vButton'
import ActionLink from '@/ui/controls/ActionLink'

import vModel from '@/mixins/vModel'
import screen from '@/mixins/screen'
import hasActiveContracts from '@/mixins/hasActiveContracts'
import fetchMore from '@/mixins/fetchMore'
import confirmAction from '@/mixins/confirmAction'
import profile from '@/mixins/profile'
import validate from '@/mixins/validate'
import { notifyGraphqlError } from '@/mixins/handleGraphqlError'
import { ShowAddTerminal, ShowEditTerminalControl } from '@/mixins/displayRules'

import { TerminalsGQL } from '@/graphql/queries'
import { AddTerminalGQL, UpdateTerminalActivityGQL, UpdateTerminalGQL } from '@/graphql/mutations'

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  components: {
    ActionLink,

    ListItem,
    ListFooter,
    ListItemGroup,
    ListItemContent,
    ListItemTitle,
    ListItemSubtitle,

    vToggle,
    Autocomplete,
    vInput,
    vButton,
    KebabMenu,
    vModal
  },

  mixins: [
    vModel,
    screen,
    hasActiveContracts,
    fetchMore,
    confirmAction,
    profile,
    validate,
    notifyGraphqlError,

    ShowAddTerminal,
    ShowEditTerminalControl
  ],

  data () {
    return {
      paginationVars: {
        page: 0,
        size: 20
      },

      newTerminal: {
        number: null,
        shopId: null
      },

      newTerminalError: {
        number: null,
        shopId: null
      },

      addingTerminal: false,
      showAddingTerminal: false,

      editTerminal: {
        id: null,
        number: null,
        shopId: null
      },

      editTerminalError: {
        number: null,
        shopId: null
      },

      updatingTerminal: false,
      showEditingTerminal: false
    }
  },

  apollo: {
    terminals: {
      query: TerminalsGQL,
      variables () {
        return {
          params: {
            filter: {},
            ...this.paginationVars
          }
        }
      },

      update: data => data.merchant.terminals,

      skip () { return !this.hasActiveContracts || !this.value }
    }
  },

  computed: {
    show: {
      get () {
        return this.value
      },

      set (val) {
        this.$emit('input', val)
      }
    },

    loading () {
      return this.$apollo.queries.terminals.loading && !this.fetchingMore
    },

    terminalList () {
      if (!this.terminals) {
        return []
      }

      const cutAddressLength = this.screen.breakPoint.tabPort ? 35 : 15
      const cutNumberLength = this.screen.breakPoint.tabPort ? 29 : 10
      const cutStoreLength = this.screen.breakPoint.tabPort ? 50 : 30

      return this.terminals.content.map(t => {
        const number = t.number.toString()
        t.computedNumber = number && number.length > cutNumberLength ? number.substr(0, cutNumberLength - 3) + '...' : number
        t.computedAddress = t.address && t.address.length > cutAddressLength ? t.address.substr(0, cutAddressLength - 3) + '...' : t.address
        t.computedShopName = t.shopName && t.shopName.length > cutStoreLength ? t.shopName.substr(0, cutStoreLength - 3) + '...' : t.shopName

        return t
      })
    }
  },

  methods: {
    showMore () {
      const params = {
        filter: {},
        ...this.paginationVars
      }

      if (!this.terminals) { return }

      this.fetchMore({
        params,
        rootField: 'merchant',
        apolloQueryField: 'terminals',
        currPage: this.terminals.currentPage
      })
    },

    async toggleActivityExec (val, item) {
      if (this.profile.status !== 'READY') {
        this.$notify({
          message: this.errorCodes.terminalEditOnlyREADY,
          type: 'error'
        })

        return
      }

      try {
        await this.$apollo.mutate({
          mutation: UpdateTerminalActivityGQL,
          variables: {
            id: item.id
          }
        })

        const message = val ? this.$t('errors.code.terminalIsActivated') : this.$t('errors.code.terminalIsDeactivated')
        this.$notify({
          message
        })
      } catch (e) {
        this.notifyGraphqlError(e)
      }
    },

    async toggleActivity (val, item) {
      if (!val) {
        this.confirmAction(this.$t('components.TerminalList.areYouSureToDeactivateTerminal'), async () => {
          await this.toggleActivityExec(val, item)
        })
      } else {
        await this.toggleActivityExec(val, item)
      }
    },

    hasAddErrors () {
      let hasErrors = false
      for (const key of Object.keys(this.newTerminal)) {
        const err = this.validate(this.newTerminal[key]).notEmpty()
        if (err) {
          this.newTerminalError[key] = err
          hasErrors = true
        }
      }

      return hasErrors
    },

    hasEditErrors () {
      let hasErrors = false
      for (const key of Object.keys(this.editTerminal)) {
        if (key === 'id') continue

        const err = this.validate(this.editTerminal[key]).notEmpty()
        if (err) {
          this.editTerminalError[key] = err
          hasErrors = true
        }
      }

      return hasErrors
    },

    async addTerminal () {
      if (this.hasAddErrors()) { return }

      try {
        this.addingTerminal = true
        await this.$apollo.mutate({
          mutation: AddTerminalGQL,
          variables: {
            params: {
              ...this.newTerminal
            }
          },
          update: (store, { data: { addTerminal } }) => {
            const { merchant } = store.readQuery({ query: TerminalsGQL, variables: { params: { filter: {}, ...this.paginationVars } } })

            const data = {
              merchant: {
                ...merchant,
                terminals: {
                  ...merchant.terminals,
                  totalElements: merchant.terminals.totalElements + 1,
                  content: [addTerminal, ...merchant.terminals.content]
                }
              }
            }

            store.writeQuery({
              query: TerminalsGQL,
              variables: { params: { filter: {}, ...this.paginationVars } },
              data: {
                ...data
              }
            })
          }
        })

        this.addingTerminal = false
        this.showAddingTerminal = false

        this.$notify({
          message: this.$t('components.TerminalList.terminalAdded')
        })

        this.newTerminal = {
          number: null,
          shopId: null
        }
      } catch (e) {
        this.addingTerminal = false
        this.notifyGraphqlError(e)
      }
    },

    handleUpdateTerminal (item) {
      this.showEditingTerminal = true
      for (const key of Object.keys(this.editTerminal)) {
        this.editTerminal[key] = item[key]
      }
    },

    async updateTerminal () {
      if (this.hasEditErrors()) { return }

      try {
        this.updatingTerminal = true
        await this.$apollo.mutate({
          mutation: UpdateTerminalGQL,
          variables: {
            params: {
              ...this.editTerminal
            }
          }
        })

        this.updatingTerminal = false
        this.showEditingTerminal = false

        this.$notify({
          message: this.$t('components.TerminalsList.terminalUpdated')
        })

        this.editTerminal = {
          id: null,
          number: null,
          shopId: null
        }
      } catch (e) {
        this.updatingTerminal = false
        this.notifyGraphqlError(e)
      }
    }
  },

  watch: {
    showAddingTerminal (val) {
      if (!val) {
        this.newTerminal = {
          number: null,
          shopId: null
        }
      }
    },

    showEditingTerminal (val) {
      if (!val) {
        this.editTerminal = {
          id: null,
          number: null,
          shopId: null
        }
      }
    },

    editTerminal: {
      handler: function () {
        this.editTerminalError = {
          number: null,
          shopId: null
        }
      },
      deep: true
    },

    newTerminal: {
      handler: function () {
        this.newTerminalError = {
          number: null,
          shopId: null
        }
      },
      deep: true
    }
  },

  mounted () {
    document.addEventListener('reachedBottom', this.showMore)
  },

  beforeDestroy () {
    document.removeEventListener('reachedBottom', this.showMore)
  }
}
</script>

<style lang="scss">
  .terminals-list {
    &__circle {
      width: 1.2rem;
      height: 1.2rem;
      display: inline-block;
      border-radius: 50%;
      background-color: $color-space-light;

      &--active {
        background-color: $color-green;
      }
    }

    .vue-recycle-scroller__item-wrapper {
      overflow: unset;
    }
  }
</style>
